<template>
  <div class="lp-session-detail" v-if="session">
    <div v-if="dayLabel" class="lp-sd-day">{{ dayLabel }}</div>

    <div v-if="session.time || session.trackLabel" class="lp-sd-meta">
      <span v-if="session.time" class="lp-sd-time">{{ session.time }}</span>
      <span v-if="session.trackLabel" class="lp-sd-track-pill" :data-track="session.trackColor">{{ session.trackLabel }}</span>
    </div>

    <h2 class="lp-sd-title" v-html="title"></h2>

    <div v-if="tags.length" class="lp-sd-tags">
      <span class="lp-sd-tag" v-for="(tag, index) in tags" :key="index">{{ tag }}</span>
    </div>

    <div v-if="session.description" class="lp-sd-section">
      <h3 class="lp-sd-section-label">About this session</h3>
      <div class="lp-sd-body" v-html="session.description"></div>
    </div>

    <div v-if="objectives.length" class="lp-sd-section">
      <h3 class="lp-sd-section-label">Learning objectives</h3>
      <ol class="lp-sd-objectives">
        <li v-for="(objective, index) in objectives" :key="index">
          <span class="lp-sd-num">{{ index + 1 }}</span>
          <span>{{ objective }}</span>
        </li>
      </ol>
    </div>

    <div v-if="presenterList.length" class="lp-sd-section">
      <h3 class="lp-sd-section-label">Presenter{{ presenterList.length > 1 ? 's' : '' }}</h3>
      <lp-presenter-list :presenters="presenterList" @select="$emit('select-presenter', $event)" />
    </div>

    <div v-if="topics.length" class="lp-sd-section">
      <h3 class="lp-sd-section-label">Topics</h3>
      <div class="lp-sd-topics">
        <span
          v-for="(topic, index) in topics"
          :key="index"
          class="lp-sd-topic"
          :class="{ 'lp-sd-topic--h': topic.hours }"
        >{{ topic.title }}<span v-if="topic.hours" class="lp-sd-topic-h">{{ topic.hours }}h</span></span>
      </div>
    </div>

    <div v-if="approvals.length" class="lp-sd-section">
      <h3 class="lp-sd-section-label">{{ ceApprovalsLabel }}</h3>
      <ul class="lp-sd-ce-list">
        <li v-for="(approval, index) in approvals" :key="index">{{ approval.name }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
import LpPresenterList from './lp-presenter-list'

/**
 * Session detail — the body of the session drawer: day, time, track, title,
 * tags, description, learning objectives, presenters, topics and CE approvals.
 *
 * Shared by the multiple-theme landing page (components/modals.blade.php) and
 * the registration page (pages/registration/index.vue). Both pass a record from
 * ConferenceLandingPageService::transformSessionForNewTheme, fetched from the
 * same /event/agenda endpoint, so the two surfaces show the same information.
 *
 * The CE approvals heading is organization-overridable: the payload carries
 * `ceApprovalsLabel` when config/landing_labels.php renames it.
 *
 * `presenters` overrides `session.presenters` for hosts that resolve the list
 * themselves: the registration page can open this drawer from its sessions
 * step, whose payload (AgendaGroupService) carries name-only presenters and
 * `name` instead of `title`.
 */
export default {
  name: 'lp-session-detail',

  components: { LpPresenterList },

  props: {
    session: { type: Object, default: null },
    dayNames: { type: Object, default: () => ({}) },
    presenters: { type: Array, default: null },
  },

  computed: {
    // The sessions-step payload names this `name`; the agenda payload `title`.
    title() {
      return (this.session && (this.session.title || this.session.name)) || ''
    },
    dayLabel() {
      return (this.session && this.dayNames[this.session.day]) || ''
    },
    tags() {
      return (this.session && this.session.tags) || []
    },
    objectives() {
      return (this.session && this.session.objectives) || []
    },
    presenterList() {
      if (this.presenters) return this.presenters
      return (this.session && this.session.presenters) || []
    },
    topics() {
      return (this.session && this.session.topics) || []
    },
    approvals() {
      return (this.session && this.session.ceApprovals) || []
    },
    // Some organizations name this section differently (see
    // config/landing_labels.php). The backend ships the override on the
    // session record; every other payload falls back to the theme default.
    ceApprovalsLabel() {
      return (this.session && this.session.ceApprovalsLabel) || 'CE approvals'
    },
  },
}
</script>

<style lang="scss" scoped>
.lp-sd-day {
  font-size: var(--fs-label);
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 500;
  margin-bottom: 8px;
}

.lp-sd-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
  flex-wrap: wrap;
}

.lp-sd-time {
  font-size: var(--fs-small);
  font-variant-numeric: tabular-nums;
  color: var(--brand-deep);
  font-weight: 500;
}

.lp-sd-track-pill {
  font-size: var(--fs-caption);
  font-weight: 500;
  padding: 3px 9px;
  border-radius: 999px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: var(--surface-alt);
  color: var(--text-secondary);

  &[data-track="1"] { background: #e8eee5; color: #344a2f; }
  &[data-track="2"] { background: #e0eaf2; color: #1e3a5f; }
  &[data-track="3"] { background: #e1f5ee; color: #08503d; }
  &[data-track="4"] { background: #faece7; color: #712b13; }
  &[data-track="5"] { background: #eeedfe; color: #3c3489; }
}

.lp-sd-title {
  font-size: var(--fs-h3);
  font-weight: 500;
  letter-spacing: -0.01em;
  margin: 0 0 12px;
  line-height: 1.25;
  color: var(--text);
}

.lp-sd-tags {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 24px;
}

.lp-sd-tag {
  font-size: var(--fs-label);
  font-weight: 500;
  background: var(--surface-alt);
  color: var(--text-secondary);
  padding: 3px 9px;
  border-radius: var(--radius-sm);
}

.lp-sd-section {
  margin-top: 28px;
}

.lp-sd-section-label {
  font-size: var(--fs-caption);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--brand-deep);
  font-weight: 500;
  margin: 0 0 12px;
  padding-bottom: 8px;
  border-bottom: 0.5px solid var(--border);
}

// Descriptions arrive as HTML from the organizer, so the paragraph rules have
// to reach past the scope attribute that v-html children never receive.
.lp-sd-body {
  font-size: var(--fs-body);
  line-height: 1.65;
  color: var(--text);

  ::v-deep p {
    margin: 0 0 12px;

    &:last-child { margin-bottom: 0; }
  }
}

.lp-sd-objectives {
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    display: flex;
    gap: 12px;
    padding: 8px 0;
    font-size: var(--fs-body);
    line-height: 1.55;
    color: var(--text);
  }
}

.lp-sd-num {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--brand-soft);
  color: var(--text);
  font-size: var(--fs-caption);
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
}

// Presenter rows are styled by <lp-presenter-list>.

// Topic pills mirror the landing hero / CE card chips. The sage and cream-card
// vars are theme aliases, so they adapt per theme.
.lp-sd-topics {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.lp-sd-topic {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 14px;
  border-radius: 999px;
  background: var(--sage-soft);
  color: var(--text);
  font-size: var(--fs-small);
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;

  &--h { padding-right: 6px; }
}

.lp-sd-topic-h {
  padding: 3px 8px;
  border-radius: 999px;
  background: var(--cream-card);
  color: var(--sage-deep);
  font-size: var(--fs-caption);
  font-weight: 800;
}

.lp-sd-ce-list {
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    font-size: var(--fs-body);
    color: var(--text);
    padding: 6px 0;
    line-height: 1.5;
    border-bottom: 0.5px solid var(--border);

    &:last-child { border-bottom: none; }

    &::before {
      content: '\2713';
      color: var(--brand);
      font-weight: 700;
      margin-right: 10px;
    }
  }
}

@media (max-width: 800px) {
  .lp-sd-title { font-size: var(--fs-h5); }
}
</style>
